import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FileSaverService } from "ngx-filesaver";
import { forkJoin } from "rxjs";
import { FaxDirection } from "src/app/enums/fax-direction";
import { FaxStatus } from "src/app/enums/fax-status";
import { ModalName } from "src/app/enums/modal-name";
import { Timezone } from "src/app/enums/timezone";
import { IPaginationSearchParams } from "src/app/models/api/pagination-search-params";
import { IFax } from "src/app/models/fax/fax";
import { IFaxFolder } from "src/app/models/fax/fax-folder";
import { IFaxesSearchParams } from "src/app/models/fax/faxes-search-params";
import { FaxService } from "src/app/services/fax.service";
import { ModalService } from "src/app/services/modal.service";

@Component({
  selector: "app-faxes",
  templateUrl: "./faxes.component.html",
  styleUrls: ["./faxes.component.scss"],
})
export class FaxesComponent implements OnInit {
  readonly FAX_DIRECTIONS = ["All", FaxDirection.Inbound, FaxDirection.Outbound];
  readonly FAX_STATUSES = ["All", FaxStatus.Success, FaxStatus.Processing, FaxStatus.Failed];

  timezone = Timezone.Eastern;

  faxes: IFax[] = [];
  isFaxesLoading = true;
  faxesSearchParams: IFaxesSearchParams = {
    pageNumber: "1",
    pageSize: "10",
    timezone: this.timezone,
  };
  totalFaxesCount = 1;

  readonly skeletonRows = Array.from({ length: 6 });

  folders: IFaxFolder[] = [];
  foldersSearchParams: IPaginationSearchParams = {
    pageNumber: "1",
    pageSize: "10",
  };

  faxToDelete: IFax | null = null;
  faxToResend: IFax | null = null;
  fileErrorMessage = "";

  constructor(
    private readonly _faxService: FaxService,
    private readonly _fileSaverService: FileSaverService,
    private readonly _modalService: ModalService,
    private readonly _router: Router
  ) {}

  get startFaxNumber() {
    const pageNumber = Number.parseInt(this.faxesSearchParams.pageNumber, 10);
    const pageSize = Number.parseInt(this.faxesSearchParams.pageSize, 10);
    return (pageNumber - 1) * pageSize + 1;
  }

  get endFaxNumber() {
    const pageNumber = Number.parseInt(this.faxesSearchParams.pageNumber, 10);
    const pageSize = Number.parseInt(this.faxesSearchParams.pageSize, 10);
    return pageNumber * pageSize;
  }

  get isPrevPageDisabled() {
    return this.faxesSearchParams.pageNumber === "1";
  }

  get isNextPageDisabled() {
    const pageNumber = Number.parseInt(this.faxesSearchParams.pageNumber, 10);
    const pageSize = Number.parseInt(this.faxesSearchParams.pageSize, 10);
    return pageNumber * pageSize >= this.totalFaxesCount;
  }

  get timezones() {
    return Object.values(Timezone);
  }

  setFaxesDateSearchParam(type: "after" | "before", isoDate: string) {
    // End date time must be at the end of a day: 12.21.2021 23:59:59
    const date = new Date(isoDate);

    if (type === "after") {
      date.setHours(date.getHours() + 23);
      date.setMinutes(date.getMinutes() + 59);
      date.setSeconds(date.getSeconds() + 59);
    }

    this.faxesSearchParams.pageNumber = "1";
    this.faxesSearchParams[type] = date.toISOString();

    this.getFaxes();
  }

  setFaxesStatusSearchParam(status: string) {
    if (status !== "All") {
      this.faxesSearchParams.status = FaxStatus[status as FaxStatus];
    } else {
      this.faxesSearchParams.status = undefined;
    }

    this.getFaxes();
  }

  setFaxesDirectionSearchParam(direction: string) {
    if (direction !== "All") {
      this.faxesSearchParams.direction = FaxDirection[direction as FaxDirection];
    } else {
      this.faxesSearchParams.direction = undefined;
    }

    this.getFaxes();
  }

  navigateToFax(event: MouseEvent, faxId: number) {
    if (!event.defaultPrevented) {
      this._router.navigate(["/faxes", faxId]);
    }
  }

  getFaxNumber(fax: IFax) {
    return fax.direction === FaxDirection.Inbound ? fax.fromNumber : fax.toNumber;
  }

  getFolderName(folderId: number) {
    return this.folders.find(folder => folder.id === folderId)?.name ?? "-";
  }

  splitISODate(date?: string): string[] {
    if (!date) {
      return [];
    }

    const parsedDate = new Date(date);

    const datePart = `${parsedDate.getMonth() + 1}/${parsedDate.getDate()}/${parsedDate.getFullYear()}`;
    const timePart = parsedDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

    return [datePart, timePart];
  }

  getFaxStatusIconSrc(faxStatus: FaxStatus) {
    switch (faxStatus) {
      case FaxStatus.Success: {
        return "assets/img/icons/auth-success.svg";
      }
      case FaxStatus.Failed: {
        return "assets/img/icons/failed.svg";
      }
      default: {
        return "assets/img/icons/loading.svg";
      }
    }
  }

  downloadFaxFile(faxId: number) {
    this._faxService.getFileSrc(faxId.toString()).subscribe({
      next: fileSrc => {
        this.getFile(fileSrc).then(file => this._fileSaverService.save(file, `fax-${faxId}.pdf`));
      },
      error: (error: Error) => this.openFileErrorModal(error),
    });
  }

  openFaxFile(faxId: number) {
    this._faxService.getFileSrc(faxId.toString()).subscribe({
      next: fileSrc => window.open(fileSrc, "_blank", "noopener"),
      error: (error: Error) => this.openFileErrorModal(error),
    });
  }

  private openFileErrorModal(error: Error) {
    this.fileErrorMessage = error.message || "Something went wrong, please try once again.";
    this._modalService.openModal(ModalName.Error);
  }

  private async getFile(fileSrc: string) {
    const fileResponse = await fetch(fileSrc);
    return await fileResponse.blob();
  }

  openDeleteConfirmationPopup(fax: IFax) {
    this.faxToDelete = fax;
    this._modalService.openModal(ModalName.DeleteFax);
  }

  deleteFax() {
    if (this.faxToDelete === null) {
      return;
    }

    this._faxService.delete(this.faxToDelete.id.toString()).subscribe({
      next: () => this.getFaxes(),
    });
  }

  openResendModal(fax: IFax) {
    this.faxToResend = { ...fax };
  }

  prevFaxesPage() {
    const pageNumber = Number.parseInt(this.faxesSearchParams.pageNumber, 10);

    if (pageNumber > 0) {
      this.faxesSearchParams.pageNumber = (pageNumber - 1).toString();
      this.getFaxes();
    }
  }

  nextFaxesPage() {
    const pageNumber = Number.parseInt(this.faxesSearchParams.pageNumber, 10);
    const pageSize = Number.parseInt(this.faxesSearchParams.pageSize, 10);
    const fetchedFaxesCount = pageNumber * pageSize;

    if (fetchedFaxesCount < this.totalFaxesCount) {
      this.faxesSearchParams.pageNumber = (pageNumber + 1).toString();
      this.getFaxes();
    }
  }

  setTimezone(timezone: Timezone) {
    this.timezone = timezone;
    this.faxesSearchParams.pageNumber = "1";
    this.faxesSearchParams.timezone = timezone;

    this.getFaxes();
  }

  ngOnInit() {
    this.getFaxes();
    this.getFolders();
  }

  getFaxes() {
    if (this.faxesSearchParams.status === FaxStatus.Processing) {
      const pageSize = Number.parseInt(this.faxesSearchParams.pageSize, 10);

      forkJoin([
        this._faxService.getAll({ ...this.faxesSearchParams, status: FaxStatus.Processing }),
        this._faxService.getAll({ ...this.faxesSearchParams, status: FaxStatus.TelnyxProcessing }),
      ]).subscribe(([processingFaxes, telnyxProcessingFaxes]) => {
        this.faxes = [...processingFaxes.records, ...telnyxProcessingFaxes.records]
          .sort((a, b) => new Date(b.faxDate).getTime() - new Date(a.faxDate).getTime())
          .slice(0, pageSize);
        this.totalFaxesCount = processingFaxes.totalCount + telnyxProcessingFaxes.totalCount;
        this.isFaxesLoading = false;
      });

      return;
    }

    this._faxService.getAll(this.faxesSearchParams).subscribe(faxes => {
      this.faxes = faxes.records;
      this.totalFaxesCount = faxes.totalCount;
      this.isFaxesLoading = false;
    });
  }

  private getFolders() {
    this._faxService.getFolders(this.foldersSearchParams).subscribe(folders => {
      this.folders = folders.records;
    });
  }
}
